import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import {
    DEFAULT_CLOSE_TIME,
    DEFAULT_OPEN_TIME,
    OPERATING_HOUR_DAYS,
    ROOM_TYPE_OPTIONS,
    getDefaultOperatingHours,
    updateOperatingHoursByDay,
    updateOperatingHoursTimeField
} from "../../../utils/CampusBuildingsUtil";
import { useCallback } from "react";
import Button from "../../common/Button/Button";
import StandardModal from "../../common/Modal/StandardModal";
import FormLabel from "../../common/forms/FormLabel";
import FormInput from "../../common/forms/FormInput";
import { selectStyles as sharedSelectStyles } from "../../common/forms/selectStyles";

const operatingHoursContainerStyle = {
    padding: "20px",
    border: "1px solid var(--surfaces-border-divider)",
    borderRadius: "10px",
    boxShadow: "0px 2px 6px 0px rgba(0,0,0,0.10)",
};

const operatingHourRowStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
    gap: "16px"
};

const operatingHourDayStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "150px",
    minWidth: "150px"
};

const operatingHourInputsStyle = {
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    gap: "16px"
};

const EMPTY_BUILDINGS_ARRAY = [];

const validationSchema = Yup.object({
    room_name: Yup.string().required("Room name is required"),
    building_id: Yup.string().required("Building is required"),
    type: Yup.string().required("Type is required"),
    room_no: Yup.string().required("Room number is required"),
    capacity: Yup.number()
        .typeError("Capacity must be a number")
        .required("Capacity is required")
        .min(1, "Capacity must be at least 1"),
    room_description: Yup.string().nullable()
});

const AddEditRoomModal = ({
    campusId,
    show,
    onHide,
    room,
    buildings = EMPTY_BUILDINGS_ARRAY,
    onSave,
    saving
}) => {
    const isEditMode = Boolean(room?.id);
    const [inheritFromBuilding, setInheritFromBuilding] = useState(false);

    const buildingOptions = useMemo(() => {
        return buildings.map((building) => ({
            value: building.id,
            label: building.name
        }));
    }, [buildings]);

    const initialValues = useMemo(() => ({
        room_name: room?.room_name || "",
        building_id: room?.building_id || "",
        type: room?.type || "",
        room_no: room?.room_no || "",
        capacity: room?.capacity ?? 1,
        room_description: room?.room_description || "",
        operating_hours: room?.operatingHoursObj || getDefaultOperatingHours(),
        newBuildingId: room ? Number(room?.building_id) : null
    }), [room]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async (values, { setFieldError, resetForm }) => {
            const buildingExists = buildingOptions?.some(
                (option) => option.value === values.building_id
            );
            if (!buildingExists) {
                setFieldError("building_id", "Building is required");
                return;
            }

            const roomPayload = {
                id: room?.id,
                room_name: values.room_name,
                building_id: values.building_id,
                type: values.type,
                room_no: values.room_no,
                capacity: values.capacity,
                room_description: values.room_description,
                operating_hours: values.operating_hours,
                campusId: campusId,
                newBuildingId: values.newBuildingId
            };

            await onSave(roomPayload);

            resetForm();
        }
    });

    const selectedBuilding = useMemo(() => {
        return buildings.find((building) => building.id === formik.values.building_id) || null;
    }, [buildings, formik.values.building_id]);

    const handleBuildingChange = (option) => {
        const nextBuildingId = option?.value || "";
        formik.setFieldValue("building_id", nextBuildingId);
        formik.setFieldValue("newBuildingId", nextBuildingId);

        if (!inheritFromBuilding) {
            return;
        }
        
        const nextBuilding = buildings.find((b) => b.id === nextBuildingId);
        if (nextBuilding) {
            formik.setFieldValue(
                "operating_hours",
                { ...nextBuilding.operatingHoursObj } || getDefaultOperatingHours()
            );
        }
    };

    const handleInheritToggle = useCallback((e) => {
        const checked = e.target.checked;
        setInheritFromBuilding(checked);

        if (checked && selectedBuilding?.operatingHoursObj) {
            const roomBuilding = buildings.find(
                b => b.id === (formik.values.newBuildingId ? formik.values.newBuildingId : room?.building_id)
            );
            if (roomBuilding?.operatingHoursObj) {
                formik.setFieldValue(
                    "operating_hours",
                    { ...roomBuilding.operatingHoursObj }
                );
            }
        }
    }, [selectedBuilding?.operatingHoursObj, formik.values.newBuildingId, room?.building_id, buildings]);

    const handleOperatingDayToggle = (day, enabled) => {
        if (inheritFromBuilding) {
            setInheritFromBuilding(false);
        }

        const currentDay = formik.values.operating_hours[day] || {};
        const currentRange = currentDay.ranges?.[0] || { start: DEFAULT_OPEN_TIME, end: DEFAULT_CLOSE_TIME };

        const updated = updateOperatingHoursByDay({
            operatingHours: formik.values.operating_hours,
            day,
            enabled,
            timeRange: `${currentRange.start}-${currentRange.end}`
        });

        formik.setFieldValue("operating_hours", updated);
    };

    const handleOperatingTimeChange = (day, field, value) => {
        if (inheritFromBuilding) {
            setInheritFromBuilding(false);
        }

        const updated = updateOperatingHoursTimeField({
            operatingHours: formik.values.operating_hours,
            day,
            field,
            value
        });
        formik.setFieldValue("operating_hours", updated);
    };

    return (
        <StandardModal
            show={show}
            onHide={onHide}
            title={isEditMode ? "Edit Room" : "Add Room"}
            icon={<i className="fal fa-door-open" />}
            size="md"
            onSave={formik.handleSubmit}
            saveLoading={saving}
            saveDisabled={saving || !formik.isValid}
            cancelLabel="Cancel"
        >
            <form onSubmit={formik.handleSubmit}>
                {/* Basic Info Row */}
                <div className="row">
                    <div className="col-md-4">
                        <div className="form-group mb-3">
                            <FormLabel required>Name</FormLabel>
                            <FormInput
                                {...formik.getFieldProps("room_name")}
                                error={formik.errors.room_name}
                                touched={formik.touched.room_name}
                                placeholder="Enter room name"
                            />
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="form-group mb-3">
                            <FormLabel required>Campus Building</FormLabel>
                            <Select
                                options={buildingOptions}
                                value={buildingOptions.find((opt) => opt.value === formik.values.building_id) || null}
                                onChange={handleBuildingChange}
                                placeholder="Select building..."
                                className={formik.errors.building_id && formik.touched.building_id ? "is-invalid" : ""}
                                menuPortalTarget={document.body}
                                styles={sharedSelectStyles}
                            />
                            {formik.errors.building_id && formik.touched.building_id && (
                                <div className="invalid-feedback d-block">{formik.errors.building_id}</div>
                            )}
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="form-group mb-3">
                            <FormLabel required>Type</FormLabel>
                            <Select
                                options={ROOM_TYPE_OPTIONS}
                                value={ROOM_TYPE_OPTIONS.find((opt) => opt.value.toLowerCase() === formik.values.type?.toLowerCase()) || null}
                                onChange={(opt) => formik.setFieldValue("type", opt?.value || "")}
                                placeholder="Select type..."
                                className={formik.errors.type && formik.touched.type ? "is-invalid" : ""}
                                menuPortalTarget={document.body}
                                styles={sharedSelectStyles}
                            />
                            {formik.errors.type && formik.touched.type && (
                                <div className="invalid-feedback d-block">{formik.errors.type}</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Details Row */}
                <div className="row">
                    <div className="col-md-4">
                        <div className="form-group mb-3">
                            <FormLabel required>Room Number</FormLabel>
                            <FormInput
                                {...formik.getFieldProps("room_no")}
                                error={formik.errors.room_no}
                                touched={formik.touched.room_no}
                                placeholder="Enter room number"
                            />
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="form-group mb-3">
                            <FormLabel required>Capacity</FormLabel>
                            <FormInput
                                type="number"
                                {...formik.getFieldProps("capacity")}
                                error={formik.errors.capacity}
                                touched={formik.touched.capacity}
                                placeholder="Enter capacity"
                                min="1"
                            />
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="form-group mb-3">
                            <FormLabel>Description</FormLabel>
                            <FormInput
                                {...formik.getFieldProps("room_description")}
                                placeholder="Enter description"
                            />
                        </div>
                    </div>
                </div>

                {/* Operating Hours Section */}
                <div className="form-group mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <FormLabel required>Operating Hours</FormLabel>
                        <div className="custom-control custom-checkbox">
                            <input
                                type="checkbox"
                                className="custom-control-input"
                                id="inheritFromBuilding"
                                checked={inheritFromBuilding}
                                onChange={handleInheritToggle}
                            />
                            <label className="custom-control-label" htmlFor="inheritFromBuilding">
                                Inherit from campus Building
                            </label>
                        </div>
                    </div>

                    <div style={operatingHoursContainerStyle}>
                        {OPERATING_HOUR_DAYS.map((day) => {
                            const dayState = formik.values.operating_hours[day.key] || {};
                            const isEnabled = dayState.enabled || false;
                            const firstRange = dayState.ranges?.[0] || { start: DEFAULT_OPEN_TIME, end: DEFAULT_CLOSE_TIME };

                            return (
                                <div
                                    key={day.key}
                                    style={{
                                        ...operatingHourRowStyle,
                                        marginBottom: day.key === "sunday" ? 0 : operatingHourRowStyle.marginBottom
                                    }}
                                >
                                    <div style={operatingHourDayStyle}>
                                        <label className="switch mb-0">
                                            <input
                                                type="checkbox"
                                                checked={isEnabled}
                                                onChange={(e) => handleOperatingDayToggle(day.key, e.target.checked)}
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                        <span
                                            style={{
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {day.label}
                                        </span>
                                    </div>

                                    <div style={operatingHourInputsStyle}>
                                        <div className="input-group">
                                            <FormInput
                                                type="time"
                                                value={firstRange.start}
                                                disabled={!isEnabled}
                                                onChange={(e) => handleOperatingTimeChange(day.key, "start", e.target.value)}
                                            />
                                        </div>
                                        <span>-</span>
                                        <div className="input-group">
                                            <FormInput
                                                type="time"
                                                value={firstRange.end}
                                                disabled={!isEnabled}
                                                onChange={(e) => handleOperatingTimeChange(day.key, "end", e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </form>
        </StandardModal>
    );
};

export default AddEditRoomModal;